<template>
    <div class="card-details" v-if="$parent.selectedPlan">
        <h2>Payment details</h2>
        <template v-if="cards !== null">
            <ul class="content cards">
                <li class="title" v-if="cards && cards.length > 0">
                    <h4>Use an existing payment method</h4>
                </li>
                <li class="card" v-for="card in cards" :key="card.id" @click="selectedCardId = card.id" :class="{'selected': selectedCardId === card.id}">
                    <input type="radio" v-model="selectedCardId" :value="card.id" />
                    <img :src="'/images/cards/' + card.brand_img + '.png'" :alt="card.brand" />
                    <div>
                        <span>{{ card.name }}</span>
                        <div>
                            <span>&bull;&bull;&bull;&bull;&nbsp;&bull;&bull;&bull;&bull;&nbsp;&bull;&bull;&bull;&bull;&nbsp;{{ card.last4 }}</span>
                            <span class="expires">{{ card.expire }}</span>
                        </div>
                    </div>
                </li>
                <li class="action-add" v-if="selectedCardId !== null" @click="selectedCardId = null">
                    <a href="javascript:;" title="Click add a new card" @click="selectedCardId = null">
                        + Add a new payment method
                    </a>
                </li>
                <li class="add-card" v-if="selectedCardId === null">
                    <h4 v-if="cards && cards.length > 0">Add a new payment method</h4>
                    <p>Your payment details <span>🔒</span> handled by <strong>Stripe with SSL encryption</strong>.</p>
                    <div class="stripe">
                        <div ref="stripe"></div>
                    </div>
                </li>
            </ul>

            <template v-if="pricing">
                <div v-if="pricing.tax" class="calculations content">
                    <ul>
                        <li v-if="pricing.tax">
                            <strong>Price per {{ periodicity }}</strong>
                            <div class="u--right">${{ pricing.subtotal|number }} USD</div>
                        </li>
                        <li v-if="pricing.tax">
                            <strong>{{ pricing.tax }}% VAT <small v-if="!isFrance">(Add your European VAT Number to remove)</small></strong>
                            <div class="u--right">${{ pricing.tax_amount|number }} USD</div>
                        </li>
                        <li>
                            <strong>Total</strong>
                            <div class="u--right">${{ pricing.total|number }} USD</div>
                        </li>
                    </ul>
                </div>
                <div class="alert alert-error" v-if="errors">{{ errors }}</div>
                <button name="stripe" class="btn button--stripe" :disabled="sending" v-bind:class="{'button--disabled': sending}" @click="submit">
                    Subscribe for ${{ pricing.total|number }} USD/{{ periodicity }}
                </button>
            </template>
        </template>
        <template v-else>
            <p>Loading your payment informations</p>
        </template>
    </div>
</template>

<script>
import { mapState } from 'vuex'

export default {
    data () {
        return {
            cards: null,
            selectedCardId: null,
            pricing: null,
            errors: null,
            stripe: {
                instance: null,
                card: null
            },
            sending: false
        }
    },
    computed: {
        isFrance () {
            return this.account && this.account.country === 'FR'
        },
        periodicity () {
            if (!this.$parent.selectedPlan) { return null }
            return this.$parent.selectedPlan.yearly ? 'year' : 'month'
        },
        ...mapState({
            account: state => state
        })
    },
    watch: {
        selectedCardId (to) {
            if (to === null) {
                this.mountCard()
            }
            this.errors = null
        }
    },
    created () {
        this.$http.get('credits/estimate', { params: { plan: this.$route.query.plan } }).then(
            response => {
                this.pricing = response.body.details
            }
        )
        this.$http.get('account/cards/').then(
            response => {
                this.cards = response.body.cards
                if (this.cards.length > 0) {
                    let defaultCard = this.cards.find(x => x.default)
                    if (!defaultCard) {
                        defaultCard = this.cards[0]
                    }
                    this.selectedCardId = defaultCard.id
                } else {
                    this.selectedCardId = null
                    this.mountCard()
                }
            }
        )

        this.stripe.instance = window.Stripe(window.stripe_api_key)
        const elements = this.stripe.instance.elements()
        this.stripe.card = elements.create('card')
    },
    methods: {
        mountCard () {
            this.$nextTick(() => {
                this.stripe.card.mount(this.$refs.stripe)

                // Handle real-time validation errors from the card Element.
                this.stripe.card.addEventListener('change', (event) => {
                    this.errors = event.error ? event.error.message : ''
                })
            })
        },
        async submit () {
            if (this.sending) {
                return false
            }

            this.errors = null
            this.sending = true

            const form = { plan: this.$route.query.plan }

            if (this.selectedCardId === null) {
                const result = await this.stripe.instance.createToken(this.stripe.card)
                if (result.error) {
                    // Inform the user if there was an error.
                    this.errors = result.error.message
                    this.sending = false
                    return
                }

                form.token = result.token.id
            } else {
                form.card_id = this.selectedCardId
            }

            try {
                const response = await this.$http.post('account/upgrade', form)
                if (!response.body.success) {
                    // Handles SCA 3DS2/1 verification
                    // Once validated, the payment is automatically initiated
                    const result = await this.stripe.instance.confirmCardPayment(response.body.secret)
                    if (result.error) {
                        // Inform the user if there was an error.
                        this.errors = result.error.message
                        this.sending = false
                        return
                    }
                }

                try { window.plausible('Upgrade') } catch (e) {}
                this.$router.push({ name: 'upgrade-success' })
            } catch (response) {
                this.sending = false
                this.errors = 'An error occured'
                if ('error' in response.body) {
                    this.errors = response.body.error
                } else if ('errors' in response.body) {
                    this.errors = response.body.errors[Object.keys(response.body.errors)[0]][0]
                }
            }
        }
    }
}
</script>

<style lang="scss" scoped>
    @import '../../../public/css/vars';

    .card-details {
        width: 72rem;
        margin: 0 auto;
        text-align: center;

        &>p { font-style: italic; }

        .content {
            background-color: #fff;
            border-radius: 8px;
            box-shadow: $box-shadow;
            margin-bottom: 20px;
        }

        .cards {
            li {
                border-top: solid 1px lighten(#435369, 50%);
                padding: 20px;
                text-align: left;

                &:first-child {
                    border-top: none;
                }

                &.card {
                    display: flex;
                    justify-content: flex-start;
                    align-items: center;
                    cursor: pointer;

                    margin: 0 20px 20px;
                    border: solid 1px #eee;
                    border-radius: 8px;
                    transition: background-color $transition;

                    img {
                        height: 48px;
                        margin-left: 10px;
                        opacity: .75;
                    }

                    span {
                        margin-left: 40px;
                        font-family: Consolas, Menlo, Monaco, "Andale Mono WT", "Andale Mono", "Lucida Console", "Lucida Sans Typewriter", "DejaVu Sans Mono", "Bitstream Vera Sans Mono", "Liberation Mono", "Nimbus Mono L", "Courier New", Courier, monospace;
                        color: $form-text-color;
                        transition: color $transition;
                    }

                    &:not(.selected):hover {
                        background-color: #eee;
                    }
                    &.selected img { opacity: 1 }
                    &.selected span { color: $text-color }
                }

                &.action-add {
                    cursor: pointer;

                    a {
                        border: none;
                        font-size: 1.1em;
                    }
                    &:hover a {
                        color: darken($link-color, 10%)
                    }
                }

                &.add-card {
                    h4 { margin: 0 0 20px }
                    p { margin: 0 0 20px }

                    .StripeElement {
                        background:#fff;
                        padding: 20px;
                        border-radius: 5px;
                        border: 1px solid #dde0e4;
                    }

                    .StripeElement--invalid {
                        border: 1px solid $form-field-error-color;
                        color: $form-field-error-color;
                    }
                }
            }

            h4 {
                font-size: 1.8rem;
                text-align: left;
                margin: 0;
                padding: 0;
            }
        }

        .calculations {
            margin: 20px 0 10px;
            padding: 20px;
        }

        .calculations li {
            display: flex;
            justify-content: space-between;
            flex-direction: row;
            padding-top: 10px;
            margin-top: 10px;
            border-top: solid 1px $border-color-light;
            &:first-child { border-top: none; padding-top: 0; margin-top: 0 }

            small {
                font-size: 0.8em;
                font-style: italic;
                color: $text-color-light
            }
        }

        .alert {
            margin-top: 20px;
        }

        button.btn {
            margin-top: 20px;
            padding: 20px 40px;
            display: block;
            width: 100%;
            border-radius: 12px;
        }
    }
</style>
